import _inheritsLoose from "@babel/runtime/helpers/inheritsLoose";
import React, { Component } from 'react';
/**
 * Higher order component that prevents the wrapped component from rendering
 * until a given check returns true. Only defers the rendering one time: once
 * the component has mounted, deferRender will not check to see if the component
 * should be unmounted / remounted.
 *
 * @param {Object} options The options for the HOC
 * @param {Component} options.component The component to wrap
 * @param {Function} options.shouldRender Function called on componentDidUpdate.
 *                                        Should return true if the component
 *                                        is ready to render
 * @param {Function} options.onMount Function to call when the wrapper component
 *                                   mounts.
 * @return {Component} The wrapped component
 *
 * @public
 * @author Connor Taylor
 * @example
 *
 * class Button extends React.Component {
 *   render() {
 *     return (
 *       <button>
 *         { this.props.requiredData }
 *       </button>
 *     );
 *   }
 * }
 *
 * // The DeferredButton wraps the Button in a new component. The wrapped
 * // component will mount and call onMount, but the Button will only mount when
 * // shouldRender returns true (ie. when the timeout from onMount fires)
 * const DeferredButton = deferRender({
 *   component: Button,
 *   shouldRender: (props) => Boolean(props.requiredData),
 * });
 *
 * // This will render null
 * <DeferredButton requiredData={ null } />
 *
 * // This will render the actual button
 * <DeferredButton requiredData="foobar" />
 */

var deferRender = function deferRender(_ref) {
  var shouldRender = _ref.shouldRender,
      _ref$onMount = _ref.onMount,
      onMount = _ref$onMount === void 0 ? null : _ref$onMount;

  var getDisplayName = function getDisplayName(Comp) {
    return Comp.displayName || Comp.name || "Component";
  };

  return function (WrappedComponent) {
    var DeferredComponent =
    /*#__PURE__*/
    function (_Component) {
      _inheritsLoose(DeferredComponent, _Component);

      DeferredComponent.getDerivedStateFromProps = function getDerivedStateFromProps(props, state) {
        if (state.shouldMount) {
          return null;
        }

        return shouldRender(props) ? {
          shouldMount: true
        } : null;
      };

      function DeferredComponent(props) {
        var _this;

        _this = _Component.call(this, props) || this;
        _this.state = {
          shouldMount: false
        };
        return _this;
      }

      var _proto = DeferredComponent.prototype;

      _proto.componentDidMount = function componentDidMount() {
        if (onMount) {
          onMount(this.props);
        } // If shouldRender returns true on the first call, we need to ensure that
        // shouldMount gets set to true in case it returns false on the next call


        if (shouldRender(this.props)) {
          this.setState({
            shouldMount: true
          }); // eslint-disable-line
        }
      };

      _proto.render = function render() {
        return this.state.shouldMount || shouldRender(this.props) ? React.createElement(WrappedComponent, this.props) : null;
      };

      return DeferredComponent;
    }(Component);

    DeferredComponent.displayName = "Deferred(" + getDisplayName(WrappedComponent) + ")";
    return DeferredComponent;
  };
};
/** @component */


export default deferRender;